import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Shield, Clock, Heart, Award, ChevronRight, Stethoscope, MessageCircle } from 'lucide-react';
import {
  BOOKING_URL,
  CLINIC_ADDRESS_LINE_1,
  CLINIC_ADDRESS_LINE_2,
  CONTACT_EMAIL,
  CONTACT_MAILTO,
  PHONE_NUMBER,
  PHONE_TEL,
  WHATSAPP_URL,
} from '@/config/site';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const ASSETS = {
  drJay: "/images/clinicians/jay.jpg",
  drGihani: "/images/clinicians/gihani.jpg",
  drRomeed: "/images/clinicians/shihab.jpg",
  heroImage: "https://images.unsplash.com/photo-1629909613654-28e377c37b09?w=1200&q=80",
  clinicInterior: "https://images.unsplash.com/photo-1629909615184-74f495363b67?w=800&q=80",
};

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6 }
};

const staggerContainer = {
  animate: { transition: { staggerChildren: 0.1 } }
};

const specialistServices = [
  { name: 'Dental Implants', desc: 'Single, multiple, and full arch solutions' },
  { name: 'Full Arch Reconstruction', desc: 'Complete smile restoration' },
  { name: 'Bone Grafting', desc: 'Foundation for successful implants' },
  { name: 'Gingival Grafting', desc: 'Restoring healthy gum tissue' },
  { name: 'Sinus Lifting', desc: 'Preparing for upper jaw implants' },
  { name: 'Surgical Extractions', desc: 'Complex tooth removal' },
];

const faqs = [
  {
    question: "What makes Specialist Dental London different?",
    answer: "We're a specialist-led practice where complex procedures are performed by clinicians with advanced training. Our specialist team combines surgical expertise with a calm environment, always with quality in mind."
  },
  {
    question: "Can I refer a specialist case directly?",
    answer: "Yes. We accept both clinician referrals and self-referrals for specialist care. Use our guided referral form to send clinical details and attachments. Urgent referrals are flagged with [URGENT] and triaged on the same day."
  },
  {
    question: "How long does the implant process take?",
    answer: "Every case is individual. A single implant typically requires 3-6 months from placement to final crown, allowing time for proper healing. More complex cases may take longer. We'll give you a clear timeline during your consultation."
  },
  {
    question: "Do you offer general dentistry too?",
    answer: "Yes. While we're known for specialist procedures, we provide comprehensive general dentistry including check-ups, hygiene, fillings, and preventive care. Many patients appreciate having all their dental needs met in one trusted practice."
  },
  {
    question: "What should I expect at my first visit?",
    answer: "Your first appointment is planned with quality in mind. We'll discuss your concerns, review your dental history, and conduct a thorough examination. If imaging is needed, we have on-site facilities. You'll leave with a clear understanding of your options, with no pressure."
  }
];

const specialistTeamCards = [
  {
    id: 'dr-romeed',
    src: ASSETS.drRomeed,
    alt: 'Dr Shihab Romeed',
    name: 'Dr Shihab Romeed',
    role: 'Implant Surgery',
    imageClass: 'object-center',
  },
  {
    id: 'dr-bala',
    src: ASSETS.drJay,
    alt: 'Dr Jay Bala',
    name: 'Dr Jay Bala',
    role: 'Cosmetic & Implant',
    imageClass: 'object-top',
  },
  {
    id: 'dr-senanayake',
    src: ASSETS.drGihani,
    alt: 'Dr Gihani Senanayake',
    name: 'Dr Gihani Senanayake',
    role: 'Restorative Care',
    imageClass: 'object-top',
  },
];

export default function Home() {
  return (
    <div className="pb-mobile-bar" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-charcoal text-warm-white sm:min-h-screen" data-testid="hero-section">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_18%_22%,rgba(214,192,161,0.22),transparent_48%),radial-gradient(circle_at_82%_70%,rgba(255,255,255,0.1),transparent_54%)]" />
        <div className="pointer-events-none absolute -top-20 right-[-5rem] h-64 w-64 rounded-full bg-accent-gold/20 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-20 left-[-4rem] h-72 w-72 rounded-full bg-stone-500/30 blur-3xl" />

        <div className="container-luxury relative z-10 flex flex-col pb-6 pt-20 sm:min-h-screen sm:pb-8 sm:pt-24 lg:pb-10">
          <div className="grid flex-1 items-start gap-7 sm:items-center lg:grid-cols-[1.05fr_0.95fr] lg:gap-10">
            <div className="max-w-2xl">
              <motion.p
                {...fadeInUp}
                className="mb-4 inline-flex items-center gap-1 sm:gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1.5 font-accent text-[10px] uppercase tracking-[0.16em] text-accent-gold sm:px-4 sm:py-2 sm:text-xs sm:tracking-[0.2em]"
              >
                <span className="h-1.5 w-1.5 rounded-full bg-accent-gold" />
                <span className="sm:hidden">Specialist Care</span>
                <span className="hidden sm:inline">Specialist Team Care</span>
                <span className="h-1.5 w-1.5 rounded-full bg-accent-gold" />
                <span className="font-accent text-[10px] uppercase tracking-[0.16em] text-accent-gold-light sm:text-xs sm:tracking-[0.2em]">
                  New Malden, London
                </span>
              </motion.p>

              <motion.h1
                {...fadeInUp}
                transition={{ delay: 0.1 }}
                className="mb-4 font-heading text-[2.05rem] leading-[1.05] tracking-tight sm:text-[3rem] lg:text-6xl"
              >
                Specialist Dentistry,
                {' '}
                <span className="text-accent-gold">Human by Design</span>
              </motion.h1>

              <motion.p
                {...fadeInUp}
                transition={{ delay: 0.2 }}
                className="max-w-xl font-body text-[0.97rem] leading-relaxed text-stone-200 sm:text-lg md:text-xl"
              >
                Advanced surgical and general dental care in one practice. Fast referrals, clear planning,
                and a calm, highly experienced team.
              </motion.p>

              <motion.div
                {...fadeInUp}
                transition={{ delay: 0.3 }}
                className="mt-6 grid grid-cols-1 gap-3 sm:flex sm:flex-wrap"
              >
                <a
                  href={BOOKING_URL}
                  data-testid="hero-book-btn"
                  className="hero-book-premium inline-flex w-full items-center justify-center gap-2 sm:w-auto"
                >
                  Book Appointment
                  <ArrowRight size={18} strokeWidth={1.5} />
                </a>
                <Link
                  to="/referrals"
                  data-testid="hero-referral-btn"
                  className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-full border border-white/25 bg-white/10 px-8 font-body font-medium text-warm-white transition-all duration-300 hover:bg-white/20 sm:w-auto"
                >
                  Patient Referral
                  <Stethoscope size={17} strokeWidth={1.5} />
                </Link>
              </motion.div>

              <motion.div
                {...fadeInUp}
                transition={{ delay: 0.35 }}
                className="mt-4"
              >
                <Link
                  to="/services"
                  data-testid="hero-services-btn"
                  className="inline-flex items-center gap-2 font-body text-sm font-medium text-warm-white/90 transition-colors hover:text-warm-white"
                >
                  Explore Services
                  <ChevronRight size={16} strokeWidth={1.5} />
                </Link>
              </motion.div>

              <motion.div
                {...fadeInUp}
                transition={{ delay: 0.4 }}
                className="mt-5 grid grid-cols-3 gap-2 sm:max-w-md"
              >
                {[
                  { value: '24h', label: 'Referral response' },
                  { value: '3', label: 'Specialists' },
                  { value: 'All Care', label: 'under one roof' },
                ].map((item) => (
                  <div key={item.label} className="rounded-xl border border-white/15 bg-white/10 px-2.5 py-2.5 text-center backdrop-blur-sm sm:px-3 sm:py-3">
                    <p className="font-heading text-lg leading-none text-warm-white sm:text-xl">{item.value}</p>
                    <p className="mt-1 font-body text-[10px] leading-tight text-stone-200 sm:text-xs">{item.label}</p>
                  </div>
                ))}
              </motion.div>

              <motion.div
                {...fadeInUp}
                transition={{ delay: 0.45 }}
                className="mt-5 flex items-center gap-3 lg:hidden"
              >
                <div className="flex -space-x-3">
                  {[
                    { id: 'dr-romeed', src: ASSETS.drRomeed, alt: 'Dr Shihab Romeed' },
                    { id: 'dr-bala', src: ASSETS.drJay, alt: 'Dr Jay Bala' },
                    { id: 'dr-senanayake', src: ASSETS.drGihani, alt: 'Dr Gihani Senanayake' },
                  ].map((person) => (
                    <Link
                      key={person.alt}
                      to={`/team?clinician=${person.id}`}
                      aria-label={`View ${person.alt} bio`}
                      className="block focus-visible:outline-none"
                    >
                      <img
                        src={person.src}
                        alt={person.alt}
                        className="h-16 w-16 rounded-full border-2 border-charcoal object-cover transition-transform duration-300 hover:scale-105"
                      />
                    </Link>
                  ))}
                </div>
                <p className="font-body text-xs text-stone-200">Your specialist team</p>
              </motion.div>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="relative hidden lg:block"
            >
              <div className="relative h-[560px]">
                <div className="pointer-events-none absolute -left-8 top-20 h-64 w-64 rounded-full bg-accent-gold/25 blur-3xl" />
                <div className="pointer-events-none absolute -right-10 top-28 h-72 w-72 rounded-full bg-stone-200/20 blur-3xl" />

                <div className="absolute left-1/2 top-4 -mt-[10px] -translate-x-1/2 rounded-full border border-white/25 bg-white/10 px-4 py-2 backdrop-blur-sm">
                  <p className="font-accent text-[10px] uppercase tracking-[0.2em] text-accent-gold">Meet Your Specialist Team</p>
                </div>

                <div className="absolute left-1/2 top-14 z-10 w-[95%] max-w-[740px] -translate-x-1/2">
                  <div className="grid grid-cols-3 gap-2">
                    {specialistTeamCards.map((person) => (
                      <Link
                        key={person.alt}
                        to={`/team?clinician=${person.id}`}
                        aria-label={`View ${person.name} full bio`}
                        className="group block focus-visible:outline-none"
                      >
                        <motion.article
                          whileHover={{ y: -10, scale: 1.01 }}
                          transition={{ duration: 0.35, ease: 'easeOut' }}
                          className="relative h-[370px] overflow-hidden rounded-[1.4rem] border border-white/25 bg-white shadow-[0_18px_36px_rgba(0,0,0,0.32)] transition-all duration-500 group-hover:border-accent-gold/60 group-hover:shadow-[0_28px_58px_rgba(0,0,0,0.44)] xl:h-[388px]"
                        >
                          <motion.img
                            src={person.src}
                            alt={person.alt}
                            whileHover={{ scale: 1.11 }}
                            transition={{ duration: 0.55, ease: 'easeOut' }}
                            className={`h-full w-full object-cover [image-rendering:auto] [backface-visibility:hidden] [transform:translateZ(0)] ${person.imageClass}`}
                          />
                          <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-accent-gold/25 via-transparent to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
                          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-charcoal/90 via-charcoal/70 to-transparent px-3 py-3">
                            <p className="truncate font-body text-[11px] font-semibold leading-tight text-white">{person.name}</p>
                            <p className="font-body text-[10px] text-stone-200">{person.role}</p>
                          </div>
                        </motion.article>
                      </Link>
                    ))}
                  </div>
                </div>

                <div className="absolute bottom-0 left-1/2 z-20 w-[95%] max-w-[740px] -translate-x-1/2 text-center">
                  <p className="mb-3 mt-[14px] font-body text-xs text-stone-200">Three clinicians, one coordinated plan</p>
                  <div className="mb-3 grid grid-cols-3 gap-2">
                    {[
                      'Specialist oral surgery',
                      'Complex restorations',
                      'General care continuity',
                    ].map((item) => (
                      <span key={item} className="rounded-full border border-white/20 bg-charcoal/55 px-4 py-1.5 text-center font-body text-[10px] text-stone-200">
                        {item}
                      </span>
                    ))}
                  </div>
                  <Link
                    to="/team"
                    className="inline-flex items-center gap-1.5 font-body text-xs font-medium text-accent-gold transition-colors hover:text-accent-gold-light"
                  >
                    View Full Team
                    <ChevronRight size={14} strokeWidth={1.5} />
                  </Link>
                </div>
              </div>
            </motion.div>
          </div>

          <div className="mt-auto hidden border-t border-white/10 pt-5 md:block" data-testid="trust-strip">
            <div className="flex flex-wrap items-center justify-center gap-8 text-warm-white/80 lg:gap-14">
              <span className="font-body text-sm">Specialist Implant Surgery</span>
              <span className="h-1 w-1 rounded-full bg-accent-gold" />
              <span className="font-body text-sm">Full Arch Reconstruction</span>
              <span className="h-1 w-1 rounded-full bg-accent-gold" />
              <span className="font-body text-sm">Gentle General Dentistry</span>
              <span className="h-1 w-1 rounded-full bg-accent-gold" />
              <span className="font-body text-sm font-semibold text-accent-gold">New Malden, London</span>
            </div>
          </div>
        </div>
      </section>

      {/* Specialist Team Care */}
      <section className="section-spacing section-hero-echo-light" data-testid="specialist-section">
        <div className="container-luxury">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <p className="text-caption mb-4 text-accent-gold">Specialist Team Care</p>
              <h2 className="text-headline mb-6">
                Advanced Surgical Dentistry
              </h2>
              <p className="text-body-large mb-8">
                Our specialist team brings hospital-level expertise to complex procedures.
                We focus on restoring function and confidence through
                meticulous, patient-centred care.
              </p>

              <div className="grid sm:grid-cols-2 gap-4 mb-8">
                {specialistServices.map((service, i) => (
                  <div key={i} className="flex items-start gap-3 p-4 bg-white rounded-xl border border-stone-100">
                    <div className="w-2 h-2 bg-accent-gold rounded-full mt-2 flex-shrink-0" />
                    <div>
                      <h4 className="font-body font-medium text-charcoal text-sm">{service.name}</h4>
                      <p className="font-body text-xs text-stone-500 mt-1">{service.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <Link to="/services" data-testid="specialist-learn-more" className="inline-flex items-center gap-2 text-charcoal font-body font-medium link-underline">
                View All Specialist Services
                <ChevronRight size={16} strokeWidth={1.5} />
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <div className="aspect-[4/5] rounded-2xl overflow-hidden">
                <img
                  src={ASSETS.clinicInterior}
                  alt="Modern dental treatment room"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-xl shadow-xl max-w-[280px]">
                <p className="font-heading text-3xl text-charcoal mb-1">Specialist-Led</p>
                <p className="font-body text-sm text-stone-600">
                  Complex procedures performed by clinicians with advanced surgical training
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* General Dentistry */}
      <section className="section-spacing section-hero-echo-light-alt" data-testid="general-dentistry-section">
        <div className="container-luxury">
          <div className="mx-auto mb-12 max-w-3xl text-center">
            <p className="text-caption mb-4 text-accent-gold">Complete Care</p>
            <h2 className="text-headline mb-5">General Dentistry, Elevated</h2>
            <p className="text-body-large">
              Everyday dentistry delivered with specialist-level attention, continuity, and care.
            </p>
          </div>

          <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {[
              {
                title: 'Preventive Care',
                desc: 'Routine appointments that keep your mouth healthy and reduce future treatment.',
                icon: Shield,
                items: ['Check-ups & examinations', 'Professional hygiene', 'Oral health planning'],
              },
              {
                title: 'Restorative',
                desc: 'Comfort-focused treatment to repair teeth and restore reliable function.',
                icon: Stethoscope,
                items: ['Fillings & repairs', 'Crowns & bridges', 'Root canal treatment'],
              },
              {
                title: 'Cosmetic',
                desc: 'Natural enhancements designed around your features and smile goals.',
                icon: Award,
                items: ['Teeth whitening', 'Veneers', 'Smile design'],
              },
            ].map((category, i) => (
              <motion.article
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="rounded-2xl border border-stone-200 bg-white p-6 shadow-[0_8px_20px_rgba(41,37,36,0.06)] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_12px_24px_rgba(41,37,36,0.1)]"
              >
                <div>
                  <div className="mb-4 inline-flex h-10 w-10 items-center justify-center rounded-full bg-stone-100">
                    <category.icon size={17} strokeWidth={1.6} className="text-accent-gold" />
                  </div>
                  <h3 className="mb-3 font-heading text-2xl text-charcoal">{category.title}</h3>
                  <p className="mb-5 font-body text-sm leading-relaxed text-stone-600">{category.desc}</p>
                  <ul className="space-y-2">
                    {category.items.map((item, j) => (
                      <li key={j} className="flex items-start gap-2.5">
                        <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-accent-gold" />
                        <span className="font-body text-sm text-stone-600">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.article>
            ))}
          </div>

          <div className="mt-9 text-center">
            <Link to="/services" data-testid="general-services-btn" className="btn-secondary inline-flex items-center gap-2">
              Explore All Services
              <ChevronRight size={16} strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      </section>

      {/* Meet the Clinicians Preview */}
      <section className="section-spacing section-hero-echo-dark" data-testid="clinicians-preview">
        <div className="container-luxury">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <p className="font-accent text-sm tracking-widest uppercase text-accent-gold mb-4">Our Team</p>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight text-warm-white mb-6">
              Meet Your Clinicians
            </h2>
            <p className="font-body text-lg md:text-xl leading-relaxed text-stone-300">
              A team united by expertise and genuine care. Each clinician brings 
              unique strengths, and together we cover every aspect of modern dentistry.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                id: 'dr-romeed',
                name: 'Dr Shihab Romeed',
                role: 'Implant Surgeon',
                image: ASSETS.drRomeed,
              },
              {
                id: 'dr-senanayake',
                name: 'Dr Gihani Senanayake',
                role: 'General Dentist',
                image: ASSETS.drGihani,
              },
              {
                id: 'dr-bala',
                name: 'Dr Jay Bala',
                role: 'Cosmetic & Implant Dentist',
                image: ASSETS.drJay,
              },
            ].map((clinician, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                whileHover={{ y: -6 }}
                whileTap={{ y: -3, scale: 0.985 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="group"
              >
                <Link
                  to={`/team?clinician=${clinician.id}`}
                  aria-label={`View ${clinician.name} full bio`}
                  className="block transition-transform duration-200 focus-visible:outline-none active:scale-[0.99]"
                >
                  <div className="card-clinician mb-4 border border-white/10 shadow-[0_12px_28px_rgba(0,0,0,0.28)] transition-all duration-500 md:group-hover:border-accent-gold/55 md:group-hover:shadow-[0_22px_40px_rgba(0,0,0,0.38)]">
                    <motion.img
                      src={clinician.image}
                      alt={clinician.name}
                      whileHover={{ scale: 1.08 }}
                      whileTap={{ scale: 1.06 }}
                      transition={{ duration: 0.45, ease: 'easeOut' }}
                      className="w-full h-full object-cover"
                    />
                    <div className="image-overlay transition-opacity duration-500 md:group-hover:opacity-90" />
                    <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-accent-gold/25 via-transparent to-transparent opacity-0 transition-opacity duration-500 md:group-hover:opacity-100" />
                  </div>
                  <h3 className="font-heading text-xl text-warm-white">{clinician.name}</h3>
                  <p className="font-body text-sm text-stone-400">{clinician.role}</p>
                </Link>
              </motion.div>
            ))}
          </div>

        </div>
      </section>

      {/* Why Choose Us */}
      <section className="section-spacing section-hero-echo-light" data-testid="why-choose-us">
        <div className="container-luxury">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-caption mb-4 text-accent-gold">Why Choose Us</p>
              <h2 className="text-headline mb-6">
                Specialist Excellence, Personal Care
              </h2>
              <p className="text-body-large mb-6">
                At Specialist Dental London, we bring hospital-level expertise to a 
                welcoming environment with quality in mind. Every patient receives the same 
                meticulous attention, regardless of the procedure.
              </p>
              <p className="text-body mb-8">
                Our clinicians hold advanced qualifications in implantology, oral surgery, 
                and cosmetic dentistry. We invest in the latest technology and techniques 
                to deliver predictable, lasting results.
              </p>
              <Link to="/services" data-testid="why-services-link" className="btn-primary inline-flex items-center gap-2">
                Explore Our Services
                <ArrowRight size={18} strokeWidth={1.5} />
              </Link>
            </div>
            <div className="relative">
              <div className="pointer-events-none absolute -inset-4 rounded-3xl bg-gradient-to-br from-accent-gold/20 via-transparent to-charcoal/10 blur-2xl" />
              <div className="relative grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  {
                    title: 'Specialist-Led',
                    desc: 'Advanced surgical training for complex restorative and implant cases.',
                    metric: 'Consultant-level skill',
                    icon: Shield,
                    glow: 'from-accent-gold/30 to-transparent',
                  },
                  {
                    title: 'Modern Facility',
                    desc: 'Digital planning and contemporary clinical workflows for predictable outcomes.',
                    metric: 'Technology-driven care',
                    icon: Stethoscope,
                    glow: 'from-stone-300/50 to-transparent',
                  },
                  {
                    title: 'Quality In Mind',
                    desc: 'Appointments designed with time to listen, explain, and treat at the right pace.',
                    metric: 'Patient-first pacing',
                    icon: Clock,
                    glow: 'from-accent-gold/25 to-transparent',
                  },
                  {
                    title: 'Clear Pricing',
                    desc: 'Transparent treatment plans with clear fees and no hidden surprises.',
                    metric: 'Transparent planning',
                    icon: Award,
                    glow: 'from-stone-300/45 to-transparent',
                  },
                ].map((item, i) => (
                  <motion.div
                    key={item.title}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                    className="group relative overflow-hidden rounded-2xl border border-stone-200 bg-white p-6 shadow-[0_10px_24px_rgba(41,37,36,0.06)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_16px_30px_rgba(41,37,36,0.12)]"
                  >
                    <div className={`pointer-events-none absolute inset-0 bg-gradient-to-br ${item.glow} opacity-70`} />
                    <div className="relative">
                      <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-charcoal text-warm-white">
                        <item.icon size={18} strokeWidth={1.75} />
                      </span>
                      <p className="mt-4 font-accent text-[11px] uppercase tracking-widest text-stone-500">{item.metric}</p>
                      <h4 className="mt-2 font-heading text-xl text-charcoal">{item.title}</h4>
                      <p className="mt-2 font-body text-sm leading-relaxed text-stone-600">{item.desc}</p>
                    </div>
                    <div className="absolute bottom-0 left-0 h-1 w-full bg-gradient-to-r from-accent-gold/80 to-transparent opacity-70 transition-opacity duration-300 group-hover:opacity-100" />
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What to Expect */}
      <section className="section-spacing section-hero-echo-dark-alt" data-testid="what-to-expect">
        <div className="container-luxury">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <p className="font-accent text-sm tracking-widest uppercase text-accent-gold mb-4">Your Journey</p>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight text-warm-white mb-6">
              What to Expect
            </h2>
            <p className="font-body text-lg md:text-xl leading-relaxed text-stone-200">
              From your first enquiry to ongoing care, here's how we work.
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: '01', title: 'Enquire', desc: 'Call, WhatsApp, or book online. We respond promptly.' },
              { step: '02', title: 'Consultation', desc: 'Assessment with quality in mind, and a clear explanation of options.' },
              { step: '03', title: 'Treatment', desc: 'Precise care at your pace, with support throughout.' },
              { step: '04', title: 'Aftercare', desc: 'Follow-up, maintenance, and ongoing partnership.' },
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="relative rounded-xl border border-stone-600 bg-white/[0.06] p-6"
              >
                <span className="font-heading text-5xl text-accent-gold/50">{item.step}</span>
                <h3 className="font-heading text-xl text-warm-white mt-2 mb-2">{item.title}</h3>
                <p className="font-body text-sm leading-relaxed text-stone-200">{item.desc}</p>
                {i < 3 && (
                  <div className="hidden md:block absolute top-1/2 -right-2 h-px w-8 -translate-y-1/2 bg-stone-400" />
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="section-spacing section-hero-echo-light-alt" data-testid="faq-section">
        <div className="container-luxury">
          <div className="grid lg:grid-cols-2 gap-16">
            <div>
              <p className="text-caption mb-4 text-accent-gold">Questions?</p>
              <h2 className="text-headline mb-6">
                Frequently Asked
              </h2>
              <p className="text-body-large mb-8">
                We believe in clarity. Here are answers to the questions 
                we hear most often.
              </p>
              <Link to="/contact" data-testid="faq-contact-btn" className="btn-secondary inline-flex items-center gap-2">
                Ask Us Directly
                <ChevronRight size={16} strokeWidth={1.5} />
              </Link>
            </div>

            <div>
              <Accordion type="single" collapsible className="space-y-4">
                {faqs.map((faq, i) => (
                  <AccordionItem
                    key={i}
                    value={`faq-${i}`}
                    className="bg-white border border-stone-100 rounded-xl px-6 data-[state=open]:border-accent-gold/30"
                  >
                    <AccordionTrigger
                      data-testid={`faq-trigger-${i}`}
                      className="font-body font-medium text-charcoal text-left hover:no-underline py-4"
                    >
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="font-body text-stone-600 pb-4">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </div>
      </section>

      {/* Referrals Highlight */}
      <section className="section-spacing section-hero-echo-light" data-testid="referrals-highlight">
        <div className="container-luxury">
          <div className="grid gap-8 rounded-3xl border border-stone-200 bg-white p-6 md:p-10 lg:grid-cols-[1.15fr_0.85fr] lg:items-center">
            <div>
              <p className="text-caption mb-4 text-accent-gold">Specialist Referrals</p>
              <h2 className="text-headline mb-5">Need Specialist Input? Refer in Minutes.</h2>
              <p className="text-body mb-6 max-w-2xl">
                We accept clinician referrals and self-referrals through one guided form with secure file upload.
                Our team triages every referral quickly and responds the same day.
              </p>
              <div className="flex flex-wrap gap-3">
                <Link to="/referrals" data-testid="home-referral-btn" className="btn-primary inline-flex items-center gap-2">
                  Start a Referral
                  <ArrowRight size={18} strokeWidth={1.5} />
                </Link>
                <a href={WHATSAPP_URL} data-testid="home-referral-whatsapp" className="btn-secondary inline-flex items-center gap-2">
                  WhatsApp Triage
                  <MessageCircle size={16} strokeWidth={1.5} />
                </a>
              </div>
            </div>

            <div className="grid gap-3">
              {[
                'Secure upload for X-rays, scans, and letters',
                'One form for both clinician and self-referrals',
                'Same-day response from our specialist team',
                'Live support via chat or WhatsApp',
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3 rounded-xl border border-stone-200 bg-stone-50 p-4">
                  <Stethoscope size={18} className="mt-0.5 text-accent-gold" strokeWidth={1.5} />
                  <p className="font-body text-sm text-stone-600">{item}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Location Preview */}
      <section className="section-spacing section-hero-echo-dark" data-testid="location-preview">
        <div className="container-luxury">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-caption mb-4 text-stone-200">Find Us</p>
              <h2 className="text-headline mb-6 leading-tight">
                <span className="text-warm-white">New Malden</span>
                <span className="text-warm-white">, London</span>
              </h2>
              <p className="mb-8 font-body text-lg leading-relaxed text-stone-200 md:text-xl">
                Conveniently located with excellent transport links. 
                Just minutes from New Malden station.
              </p>
              <div className="space-y-4 mb-8">
                <p className="font-body text-warm-white">
                  {CLINIC_ADDRESS_LINE_1}<br />
                  {CLINIC_ADDRESS_LINE_2}
                </p>
                <div className="space-y-1">
                  <a href={PHONE_TEL} className="block font-body text-sm text-stone-200 transition-colors hover:text-warm-white">
                    {PHONE_NUMBER}
                  </a>
                  <a href={CONTACT_MAILTO} className="block font-body text-sm text-stone-200 transition-colors hover:text-warm-white">
                    {CONTACT_EMAIL}
                  </a>
                </div>
              </div>
              <Link
                to="/contact"
                data-testid="location-btn"
                className="inline-flex h-12 items-center gap-2 rounded-full border border-white/70 bg-white/10 px-8 font-body font-medium text-warm-white transition-all duration-300 hover:bg-white hover:text-charcoal"
              >
                Get Directions
                <ChevronRight size={16} strokeWidth={1.5} />
              </Link>
            </div>
            <div className="aspect-video rounded-xl overflow-hidden bg-stone-800">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2489.7012!2d-0.2554!3d51.3839!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNTHCsDIzJzAyLjAiTiAwwrAxNScxOS40Ilc!5e0!3m2!1sen!2suk!4v1234567890"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Specialist Dental London Location"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="section-spacing section-hero-echo-light-alt" data-testid="final-cta">
        <div className="container-luxury">
          <div className="text-center max-w-2xl mx-auto">
            <h2 className="text-headline mb-6">
              Ready to Begin?
            </h2>
            <p className="text-body-large mb-8">
              Whether you need specialist surgery or a trusted general dentist, 
              we're here. Book your consultation today.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href={BOOKING_URL} data-testid="final-book-btn" className="btn-primary inline-flex items-center justify-center gap-2">
                Book Appointment
                <ArrowRight size={18} strokeWidth={1.5} />
              </a>
              <a href={WHATSAPP_URL} data-testid="final-whatsapp-btn" className="btn-secondary inline-flex items-center justify-center gap-2">
                WhatsApp Us
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
